import { ComputedRef, MaybeRef, Ref } from 'vue'
import { Platform } from '../tables/Platform'
import { ApiUrls, PejerTableNames, ProjectSpecificTypes } from './base'
import { NestedSchemaPaths } from './HelperTypes'

export type Environment = 'production' | 'test'

export type ApiResponse<T> =
  | {
      statusCode: number
      data: T
    }
  | ErrorObject

export type ErrorObject = {
  statusCode: number
  error: string
  validationErrors?: ValidationErrorResponse['errors']
  httpError?: string
  isUnauthorized?: boolean
  rawError?: Error
  wasAborted?: boolean
  problemDetails?: ProblemDetails
}

export interface InvalidStateDetail {
  path: string
  value: string
  reason: string
}

export interface ProblemDetails {
  category: string
  code: string
  detail: string
  instance: string
  invalidParameters: any // Use a more specific type if you know the structure
  invalidState: InvalidStateDetail[] | null
  method: string
  requestId: string
  status: number
  timestamp: string
  title: string
  traceId: string
  type: string
}

export type ValidationErrorResponse = {
  type: string
  title: string
  status: 400
  errors: {
    [key: string]: string[]
  }
  traceId: string
}

export type CreatePayoutResponse = {
  amount: number
  id: number
  ledgerItems: []
  message: string
  success: boolean
}

export type DataState = {
  isError?: Ref<boolean>
  isSuccess?: Ref<boolean>
  error?: Ref<ErrorObject | null>
  isLoading?: Ref<boolean>
  url?: ApiUrls<any>
}

export type DataStateLoose = {
  isError?: MaybeRef<boolean> | ComputedRef<boolean>
  isSuccess?: MaybeRef<boolean> | ComputedRef<boolean>
  error?: MaybeRef<ErrorObject | null> | ComputedRef<ErrorObject | null>
  isLoading?: MaybeRef<boolean> | ComputedRef<boolean>
  url?: ApiUrls<any>
}

export type SignedInUser = {
  platformInfo: Platform.Full
  user: ProjectSpecificTypes['SignedInUser']
}

export type Order = 'Ascending' | 'Descending'
export type Filters<T> = {
  userFilters?: SearchQuery<T>
  staticFilters?: SearchQuery<T>
}

// export type BetterFilters<T> = {
//   userFilters?: BetterSearchQuery<T>
//   staticFilters?: BetterSearchQuery<T>
// }

// export type BetterOperatorObject = {
//   [key in FilterOperators]?: string | number | boolean | null
// }
// export type BetterSearchQuery<T> = {
//   [key in NestedSchemaPaths<T>]?: OperatorObject
// } & {
//   AND?: BetterSearchQuery<T>[]
//   OR?: BetterSearchQuery<T>[]
// }

export type BaseQueryArgs<T> = PaginationArgs & {
  filters: Filters<T>
  // }
  // & {
  //   grouping?: GroupingArgs<T>
} & SortingArgs<T>

export type AggregateFunction = 'sum' | 'count' | 'avg' | 'min' | 'max'
export type GroupingArgs<T> = {
  groupBy?: GetColumns<T>[]
  aggregateFunctions?: {
    [key in AggregateFunction]?: (GetColumns<T> | '*')[]
  }
}

export type GroupingResult<T> = Record<
  AggregateFunction,
  {
    [key in GetColumns<T> | '*']: number
  }
>

export type FilterOperators = '>' | '>=' | '<' | '<=' | '=' | '!='
export const FilterOperatorsList: FilterOperators[] = ['>', '>=', '<', '<=', '=', '!=']
export type SearchQuery<T> = {
  [key in NestedSchemaPaths<T>]?: OperatorObject | OperatorObjectValue
} & {
  AND?: SearchQuery<T>[]
  OR?: SearchQuery<T>[]
}
export type SearchQueryKeys<T> = keyof SearchQuery<T>
// export type SearchQuery<T> = {
//   [key in keyof T]?: T[key] extends object ? SearchQuery<T[key]> : OperatorObject
// }

export type OperatorObjectValue = string | number | boolean | null

export type OperatorObject = {
  '='?:
    | {
        value: OperatorObjectValue
        smartSearchEnabled?: boolean
        smartSearchValue?: string
      }
    | OperatorObjectValue
} & {
  [key in Exclude<FilterOperators, '='>]?:
    | {
        // Right hand value
        value: OperatorObjectValue
      }
    | OperatorObjectValue
}

export type SearchFunctionArgs<T> = {
  query: Partial<BaseQueryArgs<T>>
  options?: { table: PejerTableNames }
}

export type PaginationArgs = {
  page: number
  size: number
}

export type SortingArgs<T> = {
  sorter: Order
  sortBy: GetColumns<T>
}

export type GetColumns<T> = keyof T
